/**
 * Tenant branding (white-label): the application name, logo, colours and "powered by" line shown on
 * every screen and printed document. Stored as `tenants.branding` (jsonb); this module is the single
 * definition of the shape, the defaults, the validation and the CSS variables derived from it.
 *
 * Logos are stored as data: URIs (SVG, PNG, JPEG or WebP, ≤ 400 KB) so they need no file storage,
 * no authentication (the login page shows them) and embed directly into printed documents.
 */

export interface Branding {
  /** name shown in the sidebar, the browser title and the login page — e.g. "Grove ERP" */
  appName: string;
  /** product line in the footer ("Powered by Woodcraft ERP"); hidden when poweredBy is false */
  productName: string;
  tagline: string | null;
  taglineAr: string | null;
  /** horizontal logo, data: URI */
  logo: string | null;
  /** square mark for the collapsed sidebar and the browser tab; falls back to the logo */
  logoMark: string | null;
  /** buttons, links, active navigation, charts */
  primaryColor: string;
  /** highlights (active dot, KPI accents) */
  accentColor: string;
  /** sidebar background */
  sidebarColor: string;
  /** show "Powered by <productName>" in the footer and on printed documents */
  poweredBy: boolean;
}

export const DEFAULT_BRANDING: Branding = {
  appName: 'Woodcraft ERP',
  productName: 'Woodcraft ERP',
  tagline: 'Project-based manufacturing, from quotation to installation.',
  taglineAr: 'تصنيع قائم على المشروعات، من عرض السعر إلى التركيب.',
  logo: null,
  logoMark: null,
  primaryColor: '#2f6b45',
  accentColor: '#c9a227',
  sidebarColor: '#1e3a2a',
  poweredBy: true,
};

export const MAX_LOGO_BYTES = 400 * 1024;
const DATA_IMAGE = /^data:image\/(svg\+xml|png|jpeg|webp);base64,[A-Za-z0-9+/=\s]+$/;
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export interface BrandingIssue { field: keyof Branding; message: string }

/** Approximate decoded size of a base64 data: URI. */
export function dataUriBytes(uri: string): number {
  const i = uri.indexOf(',');
  const b64 = i >= 0 ? uri.slice(i + 1).replace(/\s/g, '') : '';
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

export function isDataImage(value: unknown): value is string {
  return typeof value === 'string' && DATA_IMAGE.test(value) && dataUriBytes(value) <= MAX_LOGO_BYTES;
}

/**
 * Turns whatever is stored (or submitted) into a complete Branding, reporting what was rejected.
 * Unknown keys are ignored; missing keys take the defaults; invalid values are reported and replaced
 * by the default so a bad save can never break the login page.
 */
export function normalizeBranding(input: unknown): { branding: Branding; issues: BrandingIssue[] } {
  const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const issues: BrandingIssue[] = [];
  const text = (field: keyof Branding, max: number, fallback: string | null): string | null => {
    const v = raw[field];
    if (v === undefined) return fallback;
    if (v === null || v === '') return field === 'appName' || field === 'productName' ? fallback : null;
    if (typeof v !== 'string') { issues.push({ field, message: 'must be text' }); return fallback; }
    const s = v.trim();
    if (s.length > max) { issues.push({ field, message: `must be at most ${max} characters` }); return s.slice(0, max); }
    return s || fallback;
  };
  const colour = (field: 'primaryColor' | 'accentColor' | 'sidebarColor'): string => {
    const v = raw[field];
    if (v === undefined || v === null || v === '') return DEFAULT_BRANDING[field];
    if (typeof v === 'string' && HEX.test(v.trim())) return expandHex(v.trim().toLowerCase());
    issues.push({ field, message: 'must be a hex colour like #2f6b45' });
    return DEFAULT_BRANDING[field];
  };
  const image = (field: 'logo' | 'logoMark'): string | null => {
    const v = raw[field];
    if (v === undefined || v === null || v === '') return null;
    if (isDataImage(v)) return v.replace(/\s/g, '');
    issues.push({ field, message: typeof v === 'string' && v.startsWith('data:image/') ? `image too large (max ${MAX_LOGO_BYTES / 1024} KB) or unsupported format` : 'must be an SVG, PNG, JPEG or WebP image' });
    return null;
  };
  const branding: Branding = {
    appName: text('appName', 60, DEFAULT_BRANDING.appName)!,
    productName: text('productName', 60, DEFAULT_BRANDING.productName)!,
    tagline: text('tagline', 120, DEFAULT_BRANDING.tagline),
    taglineAr: text('taglineAr', 120, DEFAULT_BRANDING.taglineAr),
    logo: image('logo'),
    logoMark: image('logoMark'),
    primaryColor: colour('primaryColor'),
    accentColor: colour('accentColor'),
    sidebarColor: colour('sidebarColor'),
    poweredBy: raw.poweredBy === undefined ? DEFAULT_BRANDING.poweredBy : Boolean(raw.poweredBy),
  };
  return { branding, issues };
}

// ----------------------------------------------------------------------------- colour helpers

export function expandHex(hex: string): string {
  const h = hex.replace('#', '');
  return `#${h.length === 3 ? h.split('').map((c) => c + c).join('') : h}`.toLowerCase();
}
export function hexToRgb(hex: string): [number, number, number] {
  const h = expandHex(hex).slice(1);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
export function rgbToHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
}
/** Mixes `hex` with `withHex` — weight 0 → hex, 1 → withHex. */
export function mix(hex: string, withHex: string, weight: number): string {
  const a = hexToRgb(hex); const b = hexToRgb(withHex);
  return rgbToHex([a[0] + (b[0] - a[0]) * weight, a[1] + (b[1] - a[1]) * weight, a[2] + (b[2] - a[2]) * weight]);
}
/** WCAG relative luminance (0 = black, 1 = white). */
export function luminance(hex: string): number {
  const lin = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a); const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/** Black or white, whichever reads better on the given background. */
export function readableOn(hex: string): '#ffffff' | '#111111' {
  return contrastRatio(hex, '#ffffff') >= contrastRatio(hex, '#111111') ? '#ffffff' : '#111111';
}

/**
 * CSS custom properties for the web application derived from the branding. Only the tokens that
 * carry identity change; layout, semantic colours (green / amber / red) and typography stay as
 * designed so every tenant gets the same usability.
 */
export function brandingCssVars(b: Branding): Record<string, string> {
  const primaryFg = readableOn(b.primaryColor);
  const sidebarLight = luminance(b.sidebarColor) > 0.4;
  const sidebarFg = sidebarLight ? '#1b2420' : '#e8efe9';
  const sidebarMuted = sidebarLight ? mix(b.sidebarColor, '#1b2420', 0.6) : mix(b.sidebarColor, '#ffffff', 0.62);
  const sidebarActive = sidebarLight ? mix(b.primaryColor, '#ffffff', 0.82) : mix(b.sidebarColor, b.primaryColor, 0.75);
  return {
    '--sidebar': b.sidebarColor,
    '--sidebar-active': sidebarActive,
    '--sidebar-fg': sidebarFg,
    '--sidebar-muted': sidebarMuted,
    '--sidebar-line': sidebarLight ? 'rgba(0, 0, 0, .08)' : 'rgba(255, 255, 255, .08)',
    '--sidebar-hover': sidebarLight ? 'rgba(0, 0, 0, .05)' : 'rgba(255, 255, 255, .06)',
    '--sidebar-active-fg': sidebarLight ? '#1b2420' : '#ffffff',
    '--primary': b.primaryColor,
    '--primary-hover': mix(b.primaryColor, '#000000', 0.15),
    '--primary-fg': primaryFg,
    '--primary-soft': mix(b.primaryColor, '#ffffff', 0.85),
    '--tint-green': mix(b.primaryColor, '#ffffff', 0.85),
    '--accent': b.accentColor,
    '--chart-1': b.primaryColor,
    '--chart-2': mix(b.primaryColor, '#ffffff', 0.45),
    '--chart-3': b.accentColor,
  };
}

/** Text of a `<style>` element applying the variables to :root. */
export function brandingStyleSheet(b: Branding): string {
  return `:root{${Object.entries(brandingCssVars(b)).map(([k, v]) => `${k}:${v}`).join(';')}}`;
}

/** The logo for a given place, with fallbacks. */
export function brandLogo(b: Branding, kind: 'logo' | 'mark'): string | null {
  return kind === 'mark' ? b.logoMark ?? b.logo : b.logo ?? b.logoMark;
}
